import { track } from '@amplitude/analytics-browser'

import './cf-tl-event'

interface QueuedEvent {
  eventName: string
  eventProperties?: Record<string, unknown>
  callback?: () => void
  timestamp: number
}

const eventQueue: QueuedEvent[] = []

export const processEventQueue = () => {
  if (eventQueue.length === 0) {
    return
  }

  while (eventQueue.length > 0) {
    const queuedEvent = eventQueue.shift()

    if (queuedEvent) {
      const { eventName, eventProperties, callback } = queuedEvent

      // Use the same routing as sendAmplitudeEvent: prefer Tag Loader's instance
      // so queued events share the same session_id as live events. Before this fix,
      // always calling track() here sent queued events through @amplitude/analytics-browser
      // while live events went through cfTLEvent.ampInstance, producing two different
      // session_ids for the same user within seconds of each other.
      if (typeof window?.cfTLEvent?.ampInstance !== 'undefined') {
        window.cfTLEvent.ampInstance.logEvent(
          eventName,
          { ...eventProperties, queued_event: true },
          callback,
        )
      } else {
        track(eventName, { ...eventProperties, queued_event: true })
        if (callback) {
          callback()
        }
      }
    }
  }
}

export const enqueueEvent = (
  eventName: string,
  eventProperties?: Record<string, unknown>,
  callback?: () => void,
) => {
  eventQueue.push({ eventName, eventProperties, callback, timestamp: Date.now() })
}

export const getEventQueue = (): QueuedEvent[] => {
  return eventQueue
}

// Poll interval (ms) while waiting for Tag Loader to attach
const TL_POLL_INTERVAL_MS = 100

// Absolute ceiling (ms) before draining regardless of Tag Loader state.
// Only ever hit if window 'load' never fires (hung iframe, etc.).
const DRAIN_CEILING_MS = 15_000

/**
 * Schedule a drain of any queued Amplitude events. Prefers routing through
 * `window.cfTLEvent.ampInstance` so queued events share the same session_id
 * as live events (see GROW-2704). Falls back to `track()` once `window.load`
 * fires without Tag Loader attaching, so events are not lost in environments
 * where Tag Loader is never loaded (e.g., Playwright runs).
 */
export const scheduleQueueDrain = () => {
  if (eventQueue.length === 0) {
    return
  }
  if (typeof window === 'undefined') {
    return
  }

  // Fast path: TL already attached.
  if (window.cfTLEvent?.ampInstance) {
    processEventQueue()
    return
  }

  let drained = false
  const drainOnce = () => {
    if (drained) {
      return
    }
    drained = true
    processEventQueue()
  }

  const pollId = window.setInterval(() => {
    if (typeof window === 'undefined') {
      return
    }
    if (drained || window.cfTLEvent?.ampInstance) {
      window.clearInterval(pollId)
      drainOnce()
    }
  }, TL_POLL_INTERVAL_MS)

  const onLoadFallback = () => {
    if (typeof window === 'undefined') {
      return
    }
    window.clearInterval(pollId)
    drainOnce()
  }

  if (document.readyState === 'complete') {
    // Page already loaded — schedule one tick later so a very-recently-attached
    // TL has a chance to register before we give up on it.
    window.setTimeout(onLoadFallback, 0)
  } else {
    window.addEventListener('load', onLoadFallback, { once: true })
  }

  // Absolute ceiling for the rare case 'load' never fires.
  window.setTimeout(onLoadFallback, DRAIN_CEILING_MS)
}
